import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Table, Badge, Form, Button, Modal, Spinner } from "react-bootstrap";
import { getDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import { downloadAuthedPdf } from "../../../utils/apiDownload";
import { exportListExcel, printListPdf } from "../../../utils/listExport";

/**
 * Council > Placement > Applications (read-only tracking). The council views
 * applications, their Application ID and status; it never changes the employer's
 * select/reject. Each row has a PDF; the list exports to Excel / PDF.
 */
const FILE_BASE = "https://nsvec.com/api/public/";
const STATUSES = ["applied", "pending", "shortlisted", "interview", "selected", "rejected", "joined"];
const token = () => localStorage.getItem("adminToken");
const badge = (s) => {
  const v = { selected: "success", joined: "success", rejected: "danger", shortlisted: "info", interview: "primary", pending: "warning" }[s] || "secondary";
  return <Badge variant={v}>{s}</Badge>;
};

export default function Applications() {
  const dispatch = useDispatch();
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);

  const load = async (toPage = page) => {
    setBusy(true);
    try {
      const res = await dispatch(getDataAPI(`admin/placement/applications?status=${status}&search=${encodeURIComponent(search)}&page=${toPage}`, token()));
      const d = res.data.data || {};
      setRows(d.data || []);
      setMeta({ current_page: d.current_page || 1, last_page: d.last_page || 1, total: d.total || 0 });
      setPage(d.current_page || 1);
    } catch (e) { alert({ error: "Could not load applications." }); }
    setBusy(false);
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [status]);

  const open = async (id) => {
    try {
      const res = await dispatch(getDataAPI(`admin/placement/applications/${id}`, token()));
      setView(res.data.data);
    } catch (e) { alert({ error: "Could not load detail." }); }
  };

  const pdf = (a) => {
    downloadAuthedPdf(`admin/placement/applications/${a.id}/pdf`, token(), `${a.application_no || "application-" + a.id}.pdf`)
      .catch(() => alert({ error: "Could not download the application PDF." }));
  };

  const columns = [
    { key: (r) => r.application_no || "-", label: "Application ID" },
    { key: "name", label: "Candidate" },
    { key: "roll_number", label: "Roll" },
    { key: "course", label: "Course" },
    { key: "job_title", label: "Job" },
    { key: "org_name", label: "Employer" },
    { key: "status", label: "Status" },
  ];

  const fetchAllRows = async () => {
    const res = await dispatch(getDataAPI(`admin/placement/applications?status=${status}&search=${encodeURIComponent(search)}&per_page=100000`, token()));
    return (res.data.data && res.data.data.data) || [];
  };
  const exportExcel = async () => { try { exportListExcel(columns, await fetchAllRows(), "placement-applications"); } catch (e) { alert({ error: "Could not export." }); } };
  const exportPdf = async () => { try { printListPdf("Placement — Applications", columns, await fetchAllRows()); } catch (e) { alert({ error: "Could not export." }); } };

  return (
    <div className="container-fluid py-4">
      <h4 className="mb-3">Placement &mdash; Applications</h4>
      <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: 12 }}>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Status</Form.Label>
          <Form.Control as="select" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 150 }}>
            <option value="all">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Form.Control>
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Search</Form.Label>
          <Form.Control value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(1)} placeholder="Name / roll" style={{ width: 220 }} />
        </Form.Group>
        <Button variant="primary" onClick={() => load(1)} disabled={busy}>Search</Button>
        <div className="ml-auto d-flex" style={{ gap: 8 }}>
          <Button variant="outline-success" size="sm" onClick={exportExcel}>Excel</Button>
          <Button variant="outline-secondary" size="sm" onClick={exportPdf}>PDF</Button>
        </div>
      </div>

      <Table bordered hover responsive size="sm">
        <thead className="thead-light">
          <tr><th>Application ID</th><th>Candidate</th><th>Roll</th><th>Course</th><th>Job</th><th>Employer</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          {busy && <tr><td colSpan={8} className="text-center py-3"><Spinner animation="border" size="sm" /></td></tr>}
          {!busy && rows.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-3">No applications.</td></tr>}
          {!busy && rows.map((a) => (
            <tr key={a.id}>
              <td>{a.application_no || <span className="text-muted">-</span>}</td>
              <td>{a.name}</td>
              <td>{a.roll_number}</td>
              <td>{a.course}</td>
              <td>{a.job_title}</td>
              <td>{a.org_name}</td>
              <td>{badge(a.status)}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <Button size="sm" variant="outline-primary" className="mr-1" onClick={() => open(a.id)}>View</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => pdf(a)}>PDF</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex align-items-center" style={{ gap: 10 }}>
        <small className="text-muted">Page {meta.current_page} of {meta.last_page} &bull; {meta.total} total</small>
        <div className="ml-auto">
          <Button size="sm" variant="light" disabled={busy || page <= 1} onClick={() => load(page - 1)}>Prev</Button>{" "}
          <Button size="sm" variant="light" disabled={busy || page >= meta.last_page} onClick={() => load(page + 1)}>Next</Button>
        </div>
      </div>

      <Modal show={!!view} onHide={() => setView(null)}>
        <Modal.Header closeButton><Modal.Title>Application {view && view.application_no ? `— ${view.application_no}` : ""}</Modal.Title></Modal.Header>
        <Modal.Body>
          {view && (
            <table className="table table-sm mb-0">
              <tbody>
                <tr><th>Application ID</th><td>{view.application_no || "-"}</td></tr>
                <tr><th>Name</th><td>{view.name}</td></tr>
                <tr><th>Roll</th><td>{view.roll_number}</td></tr>
                <tr><th>Course</th><td>{view.course}</td></tr>
                <tr><th>Mobile</th><td>{view.mobile}</td></tr>
                <tr><th>Email</th><td>{view.email}</td></tr>
                <tr><th>Qualification</th><td>{view.last_qualification}</td></tr>
                <tr><th>Experience</th><td>{view.experience}</td></tr>
                <tr><th>Preferred</th><td>{[view.pref_city, view.pref_state].filter(Boolean).join(", ")}</td></tr>
                <tr><th>Status</th><td>{badge(view.status)} {view.status_reason}</td></tr>
                <tr><th>Resume</th><td>{view.resume ? <a href={FILE_BASE + view.resume} target="_blank" rel="noopener noreferrer">Download</a> : "-"}</td></tr>
                {view.job && <tr><th>Job</th><td>{view.job.title} &mdash; {view.job.job_city}</td></tr>}
                {view.employer && <tr><th>Employer</th><td>{view.employer.org_name}</td></tr>}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          {view && <Button variant="outline-secondary" onClick={() => pdf(view)}>Download PDF</Button>}
          <Button variant="light" onClick={() => setView(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
